﻿import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { useEscKey } from "src/hooks/useEscKey";
import { useLiveRecipientDetail } from "src/hooks/useLiveRecipientDetail";
import { useLocation } from "react-router-dom";
import { sanitizeInput, trimInput, normalizePlz } from "src/helpers/sanitizeInput";
import { birthDateBounds } from "src/helpers/birthDateBounds";
import MultiPhoneInput from "src/components/PhoneInput/MultiPhoneInput";
import { FaUserInjured } from "react-icons/fa6";
import { isRecipientSameAsPatient } from "src/helpers/recipientSamePerson";
import { useRecipientForm } from "./useRecipientForm";
import { usePatientForm, canEditExternalId } from "./usePatientForm";
import { RecipientInvoicesCard } from "./RecipientInvoicesCard";
import { RecipientPatientsCard } from "./RecipientPatientsCard";
import { RecipientQuestionnaireTab } from "./RecipientQuestionnaireTab";
import { DetailPage, DetailCard, DetailRow } from "src/components/Details/Details";
import Toast, { type ToastState } from "src/components/Toast/Toast";
import Popup from "src/components/Popup/Popup";
import { apiRequest } from "src/services/apiClient";
import { useTranslation } from "react-i18next";
import { useAuth } from "src/context/AuthProvider";
import { DeleteInvoiceCascadeItem } from "src/components/DeleteInvoiceCascadeItem/DeleteInvoiceCascadeItem";
import { computeInvoiceDeleteBreakdown, formatOtherDoctorsWarning } from "src/utils/invoiceDeleteBreakdown";
import { isRecipientPostOnly } from "src/utils/postOnly";
import { formatSalutationLabel } from "src/helpers/salutation";
import { getRecipientQuestionnaireEmail } from "src/helpers/recipientQuestionnaireEmail";

import { ConsentModal } from "./ConsentModal";
import { MdOutlineMail, MdPhone, MdWarningAmber, MdSms } from "react-icons/md";
import { PiHandArrowDown } from "react-icons/pi";
import { TbDeviceFloppy, TbShieldCheck, TbClipboardCheck, TbClipboardList, TbQrcode, TbX, TbUser, TbFileEuro, TbTrash, TbChevronDown, TbSignature, TbArrowsExchange, TbUsers, TbMailOff, TbSend, TbBan, TbInfoCircle, TbAlertTriangle } from "react-icons/tb";
import { ChangeRecipientPopup } from "../PatientPage/ChangeRecipientPopup";
import { StickyCornerAction } from "src/components/StickyCornerAction/StickyCornerAction";
import { useApi } from "src/hooks/useApi";
import { IoChevronForward } from "react-icons/io5";
import { isAdmin as checkAdmin } from "src/utils/roles";
import { getTagStyle } from "src/helpers/randomColor";
import PatientGroupsPopup from "../Patients/PatientGroupsPopup";
import CreatePatientModal from "src/components/CreatePatientModal/CreatePatientModal";
import type { Group, Patient } from "src/types";

import "./recipientPage.scss";
import { CityInput } from "src/components/CityInput/CityInput";

export default function RecipientPage() {
    const {
        recipient,
        loading,
        error,
        navigate,
        id,
        reload,
        setOnSubmitted,
    } = useLiveRecipientDetail();
    const { t } = useTranslation();
    const { user } = useAuth();
    const rpRole = user?.doctor?.userRights;
    const canWrite = user?.doctor?.permRecipients !== undefined
        ? user.doctor.permRecipients
        : (rpRole === 2 || rpRole === 3);
    const safeId = id ?? "";

    const samePerson     = recipient ? isRecipientSameAsPatient(recipient) : false;
    const selfPatientId  = samePerson ? ((recipient as any)?.sameAsPatientId?.trim() ?? "") : "";
    const patientList    = recipient ? (recipient.patients ?? []).filter(Boolean) : [];
    // Dependents = patients who are NOT the recipient themselves (i.e. the children/family members)
    const dependentPatients: any[] = selfPatientId
        ? patientList.filter((p: any) => p.id !== selfPatientId)
        : patientList;
    const hasPatient = dependentPatients.length > 0;
    const patientsTabDisabled = samePerson && !hasPatient;

    const [toast, setToast] = useState<ToastState | null>(null);
    const { data: allRecipients, refetch: refetchRecipientsList } = useApi<any[]>("/recipients", 1);

    useEffect(() => {
        setOnSubmitted(() => {
            setToast({ message: t("recipients.questionnaireReceived"), variant: "success" });
            void refetchRecipientsList();
        });
        return () => setOnSubmitted(null);
    }, [t, refetchRecipientsList, setOnSubmitted]);

    const SALUTATION_OPTIONS = [
        { value: "H", label: t("form.herr") },
        { value: "F", label: t("form.frau") },
    ];

    type RecipientTab = "details" | "patients" | "invoices" | "questionnaire";
    const location = useLocation();

    const navPatientId = (location.state as any)?.patientId as string | undefined;
    const selectedPatientIdx = navPatientId
        ? dependentPatients.findIndex((p: any) => p.id === navPatientId)
        : -1;
    // patientSelected only when navPatientId actually matches a dependent (not the self-patient)
    const patientSelected = selectedPatientIdx >= 0;
    const activePatient: any = patientSelected ? (dependentPatients[selectedPatientIdx] ?? null) : null;
    // Groups belong to the Patient document — resolve whichever patient is currently in view
    // (a selected dependent, or the recipient's own linked patient record when samePerson).
    const groupsPatient: any = patientSelected
        ? activePatient
        : (selfPatientId ? patientList.find((p: any) => p.id === selfPatientId) ?? null : null);

    const isAdmin = checkAdmin(rpRole);
    // Delete is a separate, grantable permission (Team Settings' None/Edit/Edit+Delete
    // control) — Admin-only by default, but not admin-exclusive once granted.
    const canDelete = user?.doctor?.permPatientsDelete !== undefined
        ? user.doctor.permPatientsDelete
        : isAdmin;
    const isPlus = (user?.praxis?.businessPlan ?? 1) >= 2;
    const { data: settingsData } = useApi<{ groupsEnabled?: boolean; sendMethods?: string[] }>("/settings");
    const groupsEnabled = settingsData?.groupsEnabled === true;
    const groupsAvailable = (user?.praxis?.entitlements?.tagging ?? isPlus) && groupsEnabled;
    const canViewGroups = user?.doctor?.permTagsGroupsView !== undefined ? user.doctor.permTagsGroupsView : isAdmin;
    const showGroupsUI = canViewGroups && groupsAvailable;
    const postEnabled = settingsData?.sendMethods?.includes("post") ?? false;
    const { data: groupsListData } = useApi<Group[]>(showGroupsUI ? "/groups" : null);
    const groupsList = groupsListData ?? [];
    const [assignGroupsOpen, setAssignGroupsOpen] = useState(false);
    const [addPatientOpen, setAddPatientOpen] = useState(false);
    const [markAsPatientOpen, setMarkAsPatientOpen] = useState(false);

    const handleAssignGroups = async (groupIds: string[]) => {
        if (!groupsPatient) return;
        try {
            await apiRequest(`/patients/${groupsPatient.id}/groups`, { method: "PATCH", body: JSON.stringify({ groupIds }) }, { "Content-Type": "application/json" });
            await reload();
        } catch {
            setToast({ message: t("groups.assignFailed"), variant: "error" });
        }
    };

    // Same "whichever patient is currently in view" resolution as groupsPatient — a dependent
    // when one's selected, otherwise the recipient's own linked patient record (samePerson).
    const [postOnlySaving, setPostOnlySaving] = useState(false);
    const [postOnlyConfirmOpen, setPostOnlyConfirmOpen] = useState(false);
    const handleTogglePostOnly = async (value: boolean) => {
        if (!groupsPatient) return;
        setPostOnlySaving(true);
        try {
            await apiRequest(`/patients/${groupsPatient.id}`, { method: "PATCH", body: JSON.stringify({ postOnly: value }) }, { "Content-Type": "application/json" });
            await reload();
        } catch {
            setToast({ message: t("common.saveFailed"), variant: "error" });
        } finally {
            setPostOnlySaving(false);
        }
    };

    // Label for delete button: "Delete patient" when a patient is the target, "Delete recipient" for a pure recipient
    const deleteLabel = (patientSelected || samePerson)
        ? t("recipients.deleteTitle")
        : t("recipients.deleteRecipient");
    // When a specific patient is selected, show only their invoices; otherwise show all
    const displayInvoices: any[] = patientSelected
        ? (recipient?.invoices ?? []).filter((inv: any) => inv.patient?.id === activePatient?.id)
        : (recipient?.invoices ?? []);
    const initialTab = (location.state as any)?.tab as RecipientTab | undefined;
    const ALL_TABS: RecipientTab[] = ["details", "patients", "invoices", "questionnaire"];
    const [activeTab, setActiveTab] = useState<RecipientTab>(
        initialTab && ALL_TABS.includes(initialTab)
            ? initialTab
            : "details"
    );

    useEffect(() => {
        const tab = (location.state as { tab?: RecipientTab } | null)?.tab;
        if (tab && ALL_TABS.includes(tab)) {
            setActiveTab(tab);
        }
    }, [location.state, location.key]);

    useEffect(() => {
        if (activeTab === "patients" && patientsTabDisabled) {
            setActiveTab("details");
        }
    }, [activeTab, patientsTabDisabled]);

    const RECIPIENT_TAB_ICONS: Record<RecipientTab, React.ReactNode> = {
        details:       <TbUser />,
        patients:      <FaUserInjured />,
        invoices:      <TbFileEuro />,
        questionnaire: <TbClipboardList />,
    };

    const [questionnairePopup, setQuestionnairePopup] = useState(false);
    const [questionnaireSending, setQuestionnaireSending] = useState(false);
    const [smsPopup, setSmsPopup] = useState(false);
    const [smsSending, setSmsSending] = useState(false);
    const [smsLang, setSmsLang] = useState<"de" | "en">("de");
    const [qrOpen, setQrOpen] = useState(false);
    const [qrUrl, setQrUrl] = useState<string | null>(null);
    const [qrLoading, setQrLoading] = useState(false);
    useEscKey(() => setQrOpen(false), qrOpen);
    const [consentQrOpen, setConsentQrOpen] = useState(false);
    const [consentQrChannel, setConsentQrChannel] = useState<"digital" | "post">("digital");
    const [consentQrUrl, setConsentQrUrl] = useState<string | null>(null);
    const [consentQrLoading, setConsentQrLoading] = useState(false);
    useEscKey(() => setConsentQrOpen(false), consentQrOpen);
    useEffect(() => {
        if (!consentQrOpen) return;
        const iv = setInterval(() => { void reload(); }, 3000);
        return () => clearInterval(iv);
    }, [consentQrOpen, reload]);
    const signedAtForConsentQrChannel = consentQrChannel === "digital" ? recipient?.consentDigital?.signedAt : recipient?.consentPost?.signedAt;
    // A parent signing again for a newborn already has a signedAt — close only on a new one.
    const consentQrOpenedWithSignedAt = useRef<string | null | undefined>(null);
    useEffect(() => {
        if (consentQrOpen && signedAtForConsentQrChannel && signedAtForConsentQrChannel !== consentQrOpenedWithSignedAt.current) {
            setConsentQrOpen(false);
            setToast({ message: consentQrChannel === "post" ? "Postalische Einwilligung wurde digital unterzeichnet." : "Einwilligung wurde digital unterzeichnet.", variant: "success" });
        }
    }, [signedAtForConsentQrChannel, consentQrOpen, consentQrChannel]);
    const [revokeQrOpen, setRevokeQrOpen] = useState(false);
    const [revokeChannel, setRevokeChannel] = useState<"digital" | "post">("post");
    const [revokeQrUrl, setRevokeQrUrl] = useState<string | null>(null);
    const [revokeQrLoading, setRevokeQrLoading] = useState(false);
    useEscKey(() => setRevokeQrOpen(false), revokeQrOpen);
    useEffect(() => {
        if (!revokeQrOpen) return;
        const iv = setInterval(() => { void reload(); }, 3000);
        return () => clearInterval(iv);
    }, [revokeQrOpen, reload]);
    const revokedAtForChannel = revokeChannel === "digital" ? recipient?.consentDigital?.revokedAt : recipient?.consentPost?.revokedAt;
    useEffect(() => {
        if (revokeQrOpen && revokedAtForChannel) {
            setRevokeQrOpen(false);
            setToast({ message: "Einwilligung wurde vom Patienten widerrufen.", variant: "success" });
        }
    }, [revokedAtForChannel, revokeQrOpen]);
    const [revokeEmailSending, setRevokeEmailSending] = useState(false);
    const [revokeSmsSending, setRevokeSmsSending] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [changeRecipientOpen, setChangeRecipientOpen] = useState(false);
    const changeRecipientPatientId: string | null = patientSelected ? (activePatient?.id ?? null) : null;
    const [deleting, setDeleting] = useState(false);
    const [consentOpen, setConsentOpen] = useState(false);
    const [consentChannel, setConsentChannel] = useState<"digital" | "post">("post");
    const [qDropdownOpen, setQDropdownOpen]   = useState(false);
    const [einDropdownOpen, setEinDropdownOpen] = useState(false);
    const qDropdownRef   = useRef<HTMLDivElement>(null);
    const einDropdownRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!qDropdownOpen) return;
        const handler = (e: MouseEvent) => {
            if (qDropdownRef.current && !qDropdownRef.current.contains(e.target as Node)) {
                setQDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [qDropdownOpen]);
    useEffect(() => {
        if (!einDropdownOpen) return;
        const handler = (e: MouseEvent) => {
            if (einDropdownRef.current && !einDropdownRef.current.contains(e.target as Node)) {
                setEinDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [einDropdownOpen]);

    const {
        firstName, setFirstName, lastName, setLastName,
        title, setTitle, salutation, setSalutation,
        email, setEmail, tel, setTel,
        birthDate, setBirthDate, street, setStreet,
        city, setCity, plz, setPlz,
        bankAccountName, setBankAccountName,
        isDirty: recipientIsDirty, saving: recipientSaving, handleSave: handleRecipientSave,
    } = useRecipientForm(recipient, safeId, setToast, () => { void reload(); });

    const {
        firstName: patFirstName, setFirstName: setPatFirstName,
        lastName: patLastName, setLastName: setPatLastName,
        salutation: patSalutation, setSalutation: setPatSalutation,
        title: patTitle, setTitle: setPatTitle,
        gender: patGender, setGender: setPatGender,
        birthDate: patBirthDate, setBirthDate: setPatBirthDate,
        insuranceProvider, setInsuranceProvider,
        insuranceNumber, setInsuranceNumber,
        externalId: patExternalId, setExternalId: setPatExternalId,
        isDirty: patientIsDirty, saving: patientSaving, handleSave: handlePatientSave,
    } = usePatientForm(activePatient, setToast, () => { void reload(); });

    // The recipient's own patient record (same person), shown on the recipient card: the fields a
    // child's patient card has but a recipient doesn't (PatID, gender, insurance) are edited here.
    const selfPatient: any = !patientSelected && selfPatientId ? groupsPatient : null;
    const selfForm = usePatientForm(selfPatient, setToast, () => { void reload(); }, { patientOnly: true });
    const handleRecipientAndSelfSave = async () => {
        if (recipientIsDirty) await handleRecipientSave();
        if (selfForm.isDirty) await selfForm.handleSave();
    };

    const questionnaireEmail = useMemo(
        () => getRecipientQuestionnaireEmail(recipient, email),
        [recipient, email],
    );

    const recipientPhones = useMemo(() => (Array.isArray(tel) ? tel.filter(Boolean) : []), [tel]);
    const questionnairePhone = recipientPhones[0] ?? "";
    // Doctor's choice of which number to SMS when a recipient has more than one on file
    // (e.g. a parent's number entered first, the patient's own added later) — defaults to
    // the first, but must stay pickable rather than silently always using index 0.
    const [selectedSmsPhone, setSelectedSmsPhone] = useState(questionnairePhone);
    useEffect(() => {
        if (!recipientPhones.includes(selectedSmsPhone)) setSelectedSmsPhone(recipientPhones[0] ?? "");
    }, [recipientPhones]);

    const sendQuestionnaire = async () => {
        setQuestionnaireSending(true);
        try {
            if (recipientIsDirty) {
                await handleRecipientSave();
            }
            await apiRequest("/patient-portal/questionnaire/create-link", {
                method: "POST",
                body: JSON.stringify({ recipientId: safeId }),
            }, { "Content-Type": "application/json" });
            setQuestionnairePopup(false);
            setToast({ message: t("recipientPage.questionnaireSent"), variant: "success" });
            await reload();
        } catch (err: any) {
            setToast({ message: err?.message ?? t("recipientPage.questionnaireFailed"), variant: "error" });
            setQuestionnairePopup(false);
        } finally {
            setQuestionnaireSending(false);
        }
    };

    const sendQuestionnaireSms = async () => {
        setSmsSending(true);
        try {
            if (recipientIsDirty) {
                await handleRecipientSave();
            }
            await apiRequest("/patient-portal/questionnaire/send-sms", {
                method: "POST",
                body: JSON.stringify({ recipientId: safeId, lang: smsLang, phone: selectedSmsPhone }),
            }, { "Content-Type": "application/json" });
            setSmsPopup(false);
            setToast({ message: t("recipientPage.questionnaireSmsLinkSent"), variant: "success" });
            await reload();
        } catch (err: any) {
            setToast({ message: err?.message ?? t("recipientPage.questionnaireSmsFailed"), variant: "error" });
            setSmsPopup(false);
        } finally {
            setSmsSending(false);
        }
    };

    const openQr = async () => {
        setQrUrl(null);
        setQrOpen(true);
        setQrLoading(true);
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/patient-portal/questionnaire/create-qr`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
                body: JSON.stringify({ recipientId: safeId }),
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            setQrUrl(URL.createObjectURL(blob));
        } catch {
            setToast({ message: t("recipientPage.qrFailed"), variant: "error" });
            setQrOpen(false);
        } finally {
            setQrLoading(false);
        }
    };

    const handleViewConsentPdf = async (channel: "digital" | "post") => {
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/recipients/${safeId}/consent-pdf?channel=${channel}`, {
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            window.open(URL.createObjectURL(blob), "_blank");
        } catch {
            setToast({ message: "Einwilligung-PDF konnte nicht geöffnet werden.", variant: "error" });
        }
    };

    const openConsentQr = async (channel: "digital" | "post" = "digital") => {
        consentQrOpenedWithSignedAt.current = channel === "digital" ? recipient?.consentDigital?.signedAt : recipient?.consentPost?.signedAt;
        setConsentQrChannel(channel);
        setConsentQrUrl(null);
        setConsentQrOpen(true);
        setConsentQrLoading(true);
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/recipients/${safeId}/consent-qr`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
                body: JSON.stringify({ channel }),
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            setConsentQrUrl(URL.createObjectURL(blob));
        } catch {
            setToast({ message: "QR-Code konnte nicht generiert werden.", variant: "error" });
            setConsentQrOpen(false);
        } finally {
            setConsentQrLoading(false);
        }
    };

    const openRevokeQr = async (channel: "digital" | "post") => {
        setRevokeChannel(channel);
        setRevokeQrUrl(null);
        setRevokeQrOpen(true);
        setRevokeQrLoading(true);
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/recipients/${safeId}/consent-revoke-qr`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
                body: JSON.stringify({ channel }),
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            setRevokeQrUrl(URL.createObjectURL(blob));
        } catch {
            setToast({ message: "QR-Code konnte nicht generiert werden.", variant: "error" });
            setRevokeQrOpen(false);
        } finally {
            setRevokeQrLoading(false);
        }
    };

    const sendConsentRevokeEmail = async () => {
        setRevokeEmailSending(true);
        try {
            await apiRequest(`/recipients/${safeId}/consent-revoke-email`, {
                method: "POST",
                body: JSON.stringify({ channel: revokeChannel }),
            }, { "Content-Type": "application/json" });
            setToast({ message: t("recipientPage.consentRevokeEmailSent"), variant: "success" });
        } catch (err: any) {
            setToast({ message: err?.message ?? t("recipientPage.consentRevokeEmailFailed"), variant: "error" });
        } finally {
            setRevokeEmailSending(false);
        }
    };

    const sendConsentRevokeSms = async () => {
        setRevokeSmsSending(true);
        try {
            await apiRequest(`/recipients/${safeId}/consent-revoke-sms`, {
                method: "POST",
                body: JSON.stringify({ channel: revokeChannel, phone: selectedSmsPhone }),
            }, { "Content-Type": "application/json" });
            setToast({ message: t("recipientPage.consentRevokeSmsSent"), variant: "success" });
        } catch (err: any) {
            setToast({ message: err?.message ?? t("recipientPage.consentRevokeSmsFailed"), variant: "error" });
        } finally {
            setRevokeSmsSending(false);
        }
    };

    const handleViewQuestionnairePdf = async () => {
        try {
            const token    = localStorage.getItem("token");
            const praxisId = JSON.parse(localStorage.getItem("praxis") || "null")?.id;
            const doctorId = JSON.parse(localStorage.getItem("doctor") || "null")?.id;
            const { config } = await import("configs/config");
            const res = await fetch(`${config.baseUrl}/api/patient-portal/questionnaire/submission/${safeId}/pdf`, {
                headers: {
                    ...(token    && { Authorization: `Bearer ${token}` }),
                    ...(doctorId && { "X-Doctor-Id": doctorId }),
                    ...(praxisId && { "X-Praxis-Id": praxisId }),
                },
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            const url  = URL.createObjectURL(blob);
            window.open(url, "_blank");
        } catch {
            setToast({ message: t("recipientPage.questionnairePdfFailed"), variant: "error" });
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            if (patientSelected && activePatient) {
                await apiRequest(`/patients/${activePatient.id}`, { method: "DELETE" });
            } else {
                await apiRequest(`/recipients/${safeId}`, { method: "DELETE" });
            }
            navigate("/patients");
        } catch {
            setDeleting(false);
            setDeleteConfirmOpen(false);
            setToast({ message: t("recipients.failedDelete"), variant: "error" });
        }
    };

    const unsentInvoices: { id: string; readableId: string }[] = (recipient?.invoices ?? []).filter(
        (inv: any) => !inv.sentDate && (!activePatient || inv.patient?.id === activePatient.id),
    );

    const fullName = [
        recipient ? formatSalutationLabel(recipient.salutation, t) : undefined,
        recipient?.title, recipient?.firstName, recipient?.lastName,
    ].filter(Boolean).join(" ");

    const pageTitle    = activePatient
        ? `${activePatient.firstName} ${activePatient.lastName}`.trim()
        : fullName;
    const titleNode = (() => {
        if (!recipient) return undefined;
        // Viewing a specific dependent patient → "Patient Name → Recipient Name"
        if (patientSelected && activePatient) {
            return (
                <span className="detail-title--composite">
                    <FaUserInjured className="detail-title__patient-icon" />
                    <span>{activePatient.firstName} {activePatient.lastName}</span>
                    <IoChevronForward className="detail-title__arrow" />
                    <PiHandArrowDown className="detail-title__recipient-icon" />
                    <span
                        className="detail-title__recipient-name"
                        onClick={() => navigate(`/patients/${safeId}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && navigate(`/patients/${safeId}`)}
                    >
                        {fullName}
                    </span>
                </span>
            );
        }
        // Recipient is also a self-billing patient → show self badge
        if (samePerson) {
            return (
                <span className="detail-title--composite">
                    <span>{fullName}</span>
                    <span className="recipient-same-as-badge" title={t("invoicePage.samePersonBadge")}>
                        {t("invoicePage.selfPays")}
                    </span>
                </span>
            );
        }
        return undefined;
    })();

    type PatientRow = { recipientId: string; patientId: string | null; firstName?: string; lastName?: string };
    const patientRows: PatientRow[] | null = (location.state as any)?.patientRows ?? null;
    const currentPatientId: string | null = (location.state as any)?.patientId ?? null;
    const nextPatient = (() => {
        if (!safeId) return null;
        if (patientRows && patientRows.length > 1) {
            let idx = patientRows.findIndex(e => e.recipientId === safeId && e.patientId === currentPatientId);
            // Opened from the row itself (no patient picked): continue after this recipient's rows. Without this the
            // exact match failed whenever the recipient has patients, and the "next" link silently disappeared.
            if (idx < 0) idx = patientRows.map((e) => e.recipientId).lastIndexOf(safeId);
            if (idx >= 0) return patientRows[(idx + 1) % patientRows.length];
        }
        // Direct URL access: fall back to API order of recipients
        const list = Array.isArray(allRecipients) ? allRecipients : [];
        if (list.length < 2) return null;
        const idx = list.findIndex((r) => r?.id === safeId);
        if (idx < 0) return null;
        const next = list[(idx + 1) % list.length];
        return next ? { recipientId: next.id, patientId: null as string | null, firstName: next.firstName, lastName: next.lastName } : null;
    })();
    const nextPatientName = nextPatient ? [nextPatient.firstName, nextPatient.lastName].filter(Boolean).join(" ") : "";

    const smsPreviewTitle = smsLang === "en"
        ? `${user?.praxis?.name ?? ""} - Patient Questionnaire`
        : `${user?.praxis?.name ?? ""} - Patientenfragebogen`;
    const smsPreviewText = smsLang === "en"
        ? `Dear ${recipient?.firstName ?? ""} ${recipient?.lastName ?? ""}`.trim() +
          `, please complete your patient questionnaire:\n[Link]\nThis link is valid for 30 days.`
        : `Sehr geehrte(r) ${recipient?.firstName ?? ""} ${recipient?.lastName ?? ""}`.trim() +
          `, bitte füllen Sie Ihren Patientenfragebogen aus:\n[Link]\nDieser Link ist 30 Tage gültig.`;

    // Digital and postal consent are fully independent (see backend models/Recipient.ts) — each
    // dropdown reflects and manages only its own channel now, not one shared "is anything signed"
    // flag. consentDigital.valid already accounts for a real questionnaire self-submission too,
    // not just a digital-manual confirmation — see backend utils/questionnaireStatus.ts.
    const consentDigitalValid = recipient?.consentDigital?.valid ?? false;
    const consentPostValid = recipient?.consentPost?.valid ?? false;
    // Every patient needs their own consent: a child added after signing isn't named in it
    // (backend utils/consentStatus.ts). "Complete" = valid and names every patient — only then
    // is there nothing left to sign on that channel.
    const uncoveredDigital = recipient?.consentDigital?.uncoveredPatientIds ?? [];
    const uncoveredPost = recipient?.consentPost?.uncoveredPatientIds ?? [];
    const consentDigitalComplete = consentDigitalValid && uncoveredDigital.length === 0;
    const consentPostComplete = consentPostValid && uncoveredPost.length === 0;
    // Patients no valid consent names on either channel — their invoices can't be sent yet.
    const consentMissingNames = consentDigitalValid || consentPostValid
        ? (recipient?.patients ?? [])
            .filter((p) => !(consentDigitalValid && !uncoveredDigital.includes(p.id)) && !(consentPostValid && !uncoveredPost.includes(p.id)))
            .map((p) => [p.firstName, p.lastName].filter(Boolean).join(" "))
        : [];
    // Every patient under this recipient is post-only — digital consent outreach (email/SMS/QR)
    // is refused server-side, so only paper/postal consent is offered. See utils/postOnly.ts.
    const recipientPostOnly = isRecipientPostOnly(recipient);
    // Sent but the patient hasn't completed it yet — distinct from "not_sent" (nothing to
    // chase) so a stalled request reads as something needing follow-up, not just quiet.
    const questionnairePending =
        recipient?.questionnaireStatus === "sent" || recipient?.questionnaireStatus === "opened";

    // The patient number from the praxis's own software (GDT field 3000). Read-only once the
    // Praxissoftware-Abgleich set it — it's the key future syncs match on. For any other patient
    // staff can type it in, so later imports find the patient and documents can be written back.
    // `edit`: the form that saves it (a child's patient card, or the recipient's own patient record).
    const externalIdRow = (patient: Patient | null | undefined, edit?: { value: string; set: (v: string) => void }) => {
        const editable = !!edit && canEditExternalId(patient);
        const hint = t(editable ? "form.externalIdHintManual" : "form.externalIdHint");
        return (
            <DetailRow
                label={
                    <span className="recipient-external-id__label">
                        {t("form.externalId")}
                        <span className="recipient-external-id__info" title={hint} aria-label={hint}>
                            <TbInfoCircle size={14} />
                        </span>
                    </span>
                }
                value={
                    editable
                        ? <input className="recipient-input" disabled={!canWrite} value={edit.value} maxLength={20}
                            aria-label={t("form.externalId")}
                            onChange={(e) => edit.set(sanitizeInput(e.target.value))}
                            onBlur={(e) => edit.set(trimInput(e.target.value))} />
                        : patient?.externalId
                            ? <span className="recipient-external-id__value">{patient.externalId}</span>
                            : <span className="recipient-external-id__value recipient-external-id__value--empty">—</span>
                }
            />
        );
    };

    const headerActions = recipient && canWrite && (
        <>
            <div className="consent-methods">
                <div className="consent-methods__group">
                    <span className="consent-methods__label">{t("recipientPage.questionnaireSectionTitle")}</span>
                    <div className="q-dropdown" ref={qDropdownRef}>
                        <button
                            className={`recipient-action-btn recipient-action-btn--method q-dropdown__trigger${
                                consentDigitalComplete
                                    ? " recipient-action-btn--consent-signed"
                                    : questionnairePending
                                      ? " recipient-action-btn--consent-pending"
                                      : ""
                            }`}
                            onClick={() => setQDropdownOpen((v) => !v)}
                        >
                            <TbSend /> {t("recipientPage.digitalSendingLabel")}{consentDigitalComplete ? " ✓" : ""} <TbChevronDown className={`q-dropdown__arrow${qDropdownOpen ? " q-dropdown__arrow--open" : ""}`} />
                        </button>
                        {qDropdownOpen && (
                            <div className="q-dropdown__menu">
                                <button
                                    className="q-dropdown__item"
                                    onClick={() => { setQDropdownOpen(false); setQuestionnairePopup(true); }}
                                    disabled={recipientPostOnly || !questionnaireEmail}
                                    title={recipientPostOnly ? t("recipientPage.postOnlyConsentHint") : !questionnaireEmail ? t("recipients.noEmail") : undefined}
                                >
                                    <MdOutlineMail /> {recipient?.questionnaireStatus !== "not_sent" ? t("recipientPage.resendQuestionnaireEmail") : t("recipientPage.sendQuestionnaireEmail")}
                                </button>
                                <button
                                    className="q-dropdown__item"
                                    onClick={() => { setQDropdownOpen(false); setSmsPopup(true); }}
                                    disabled={recipientPostOnly || !questionnairePhone}
                                    title={recipientPostOnly ? t("recipientPage.postOnlyConsentHint") : !questionnairePhone ? t("recipients.noPhone") : undefined}
                                >
                                    <MdSms /> {recipient?.questionnaireStatus !== "not_sent" ? t("recipientPage.resendQuestionnaireSmsShort") : t("recipientPage.sendQuestionnaireSmsShort")}
                                </button>
                                <button
                                    className="q-dropdown__item"
                                    onClick={() => { setQDropdownOpen(false); openQr(); }}
                                    disabled={recipientPostOnly}
                                    title={recipientPostOnly ? t("recipientPage.postOnlyConsentHint") : undefined}
                                >
                                    <TbQrcode /> {t("recipientPage.questionnaireQrScan")}
                                </button>
                                {consentDigitalValid && (
                                    <button
                                        className="q-dropdown__item"
                                        onClick={() => { setQDropdownOpen(false); handleViewConsentPdf("digital"); }}
                                    >
                                        <TbShieldCheck /> Einwilligung öffnen (PDF)
                                    </button>
                                )}
                                {consentDigitalValid && (
                                    <button
                                        className="q-dropdown__item"
                                        onClick={() => { setQDropdownOpen(false); openRevokeQr("digital"); }}
                                    >
                                        <TbBan /> {t("recipientPage.requestConsentRevoke")}
                                    </button>
                                )}
                                <button
                                    className="q-dropdown__item"
                                    disabled={consentDigitalComplete}
                                    onClick={() => { setQDropdownOpen(false); setConsentChannel("digital"); setConsentOpen(true); }}
                                >
                                    <TbSignature /> {t("recipientPage.setConsentManually")}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <div className="consent-methods__group">
                    <span className="consent-methods__label">{t("recipientPage.consentSectionTitle")}</span>
                    <div className="q-dropdown" ref={einDropdownRef}>
                        <button
                            className={`recipient-action-btn recipient-action-btn--method q-dropdown__trigger${consentPostComplete ? " recipient-action-btn--consent-signed" : ""}`}
                            onClick={() => setEinDropdownOpen((v) => !v)}
                        >
                            <MdOutlineMail /> {t("recipientPage.postConsentLabel")}{consentPostComplete ? " ✓" : ""} <TbChevronDown className={`q-dropdown__arrow${einDropdownOpen ? " q-dropdown__arrow--open" : ""}`} />
                        </button>
                        {einDropdownOpen && (
                            <div className="q-dropdown__menu">
                                {consentPostValid && (
                                    <button
                                        className="q-dropdown__item"
                                        onClick={() => { setEinDropdownOpen(false); handleViewConsentPdf("post"); }}
                                    >
                                        <TbShieldCheck /> Einwilligung öffnen (PDF)
                                    </button>
                                )}
                                {consentPostValid && (
                                    <button
                                        className="q-dropdown__item"
                                        onClick={() => { setEinDropdownOpen(false); openRevokeQr("post"); }}
                                    >
                                        <TbBan /> {t("recipientPage.requestConsentRevoke")}
                                    </button>
                                )}
                                <button
                                    className="q-dropdown__item"
                                    disabled={consentPostComplete}
                                    onClick={() => { setEinDropdownOpen(false); setConsentChannel("post"); setConsentOpen(true); }}
                                >
                                    <TbSignature /> Manuell / vor Ort unterschreiben
                                </button>
                                <button
                                    className="q-dropdown__item"
                                    disabled={consentPostComplete}
                                    onClick={() => { setEinDropdownOpen(false); openConsentQr("post"); }}
                                >
                                    <TbQrcode /> QR-Code anzeigen
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="recipient-action-divider" />
            {changeRecipientPatientId && (
                <button
                    className="recipient-action-btn"
                    onClick={() => setChangeRecipientOpen(true)}
                >
                    <TbArrowsExchange /> {t("patientPage.changeRecipient")}
                </button>
            )}
            {!patientSelected && !samePerson && (
                <button
                    className="recipient-action-btn"
                    onClick={() => setMarkAsPatientOpen(true)}
                    title={t("recipientPage.markAsPatientHint")}
                >
                    <FaUserInjured /> {t("recipientPage.markAsPatient")}
                </button>
            )}
            {canDelete && (
                <button
                    className="recipient-action-btn recipient-action-btn--danger"
                    onClick={() => setDeleteConfirmOpen(true)}
                >
                    <TbTrash /> {deleteLabel}
                </button>
            )}
        </>
    );

    return (
        <>
            {toast && <Toast message={toast.message} variant={toast.variant} onClose={() => setToast(null)} />}
            {changeRecipientOpen && changeRecipientPatientId && (
                <ChangeRecipientPopup
                    patientId={changeRecipientPatientId}
                    currentRecipientId={safeId}
                    onClose={() => setChangeRecipientOpen(false)}
                    onChanged={(newRecipientId) => {
                        navigate(`/patients/${newRecipientId}`, {
                            state: { patientId: changeRecipientPatientId },
                        });
                    }}
                />
            )}
            {addPatientOpen && (
                <CreatePatientModal
                    recipientId={safeId}
                    onClose={() => setAddPatientOpen(false)}
                    onSaved={() => { setAddPatientOpen(false); void reload(); }}
                />
            )}
            {markAsPatientOpen && (
                <CreatePatientModal
                    recipientId={safeId}
                    linkAsSelf
                    prefill={{ salutation, title, firstName, lastName, birthDate, email, tel, street, plz, city }}
                    // Reload on close too: the patient may already exist even if linking it failed.
                    onClose={() => { setMarkAsPatientOpen(false); void reload(); }}
                    onSaved={() => {
                        setToast({ message: t("recipientPage.markAsPatientDone"), variant: "success" });
                    }}
                />
            )}
            {consentOpen && recipient && (
                <ConsentModal
                    recipientId={safeId}
                    recipientName={fullName}
                    patientNames={(recipient?.patients ?? []).map((p) => [p.title, p.firstName, p.lastName].filter(Boolean).join(" "))}
                    channel={consentChannel}
                    onClose={() => setConsentOpen(false)}
                    onSaved={() => { void reload(); }}
                />
            )}
            {assignGroupsOpen && groupsPatient && (
                <PatientGroupsPopup
                    groups={groupsList}
                    currentGroupIds={groupsPatient.groupIds ?? []}
                    onChange={handleAssignGroups}
                    onClose={() => setAssignGroupsOpen(false)}
                />
            )}
            {deleteConfirmOpen && (
                <Popup
                    title={deleteLabel}
                    message={
                        <div>
                            <p style={{ margin: 0 }}>{t("recipients.deleteConfirm")}</p>
                            {(() => {
                                const cascadeInvoices = patientSelected ? displayInvoices : (recipient?.invoices ?? []);
                                const cascadePatients = patientSelected ? [] : (recipient?.patients ?? []);
                                const breakdown = computeInvoiceDeleteBreakdown(cascadeInvoices);
                                // Only the whole-recipient delete has a reliable per-doctor breakdown
                                // (recipient.invoiceDoctorBreakdown) — the single-dependent-patient delete
                                // cascades just that one patient's invoices, which this list doesn't break out.
                                const otherDoctorsWarning = patientSelected
                                    ? null
                                    : formatOtherDoctorsWarning(t, recipient?.invoiceDoctorBreakdown ?? []);
                                if (cascadePatients.length === 0 && !breakdown && !otherDoctorsWarning) return null;
                                return (
                                    <>
                                        <p style={{ margin: "10px 0 4px", fontSize: "0.85rem" }}>{t("recipients.deleteCascadeIntro")}</p>
                                        <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "0.85rem" }}>
                                            {cascadePatients.length > 0 && (
                                                <li>
                                                    {t(
                                                        cascadePatients.length === 1
                                                            ? "recipients.deleteAlsoLinked_one"
                                                            : "recipients.deleteAlsoLinked_other",
                                                        { count: cascadePatients.length },
                                                    )}
                                                </li>
                                            )}
                                            {breakdown && <DeleteInvoiceCascadeItem t={t} breakdown={breakdown} />}
                                        </ul>
                                        {otherDoctorsWarning && (
                                            <p style={{ margin: "6px 0 0", fontSize: "0.85rem" }}>⚠ {otherDoctorsWarning}</p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>
                    }
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setDeleteConfirmOpen(false) },
                        { label: deleting ? "…" : deleteLabel, variant: "danger", onClick: handleDelete },
                    ]}
                    onClose={() => setDeleteConfirmOpen(false)}
                />
            )}
            {postOnlyConfirmOpen && (
                <Popup
                    title={t("form.postOnly")}
                    message={<p style={{ margin: 0 }}>{t("form.postOnlyConfirm")}</p>}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setPostOnlyConfirmOpen(false) },
                        {
                            label: t("popup.confirm"),
                            variant: "primary",
                            onClick: () => {
                                setPostOnlyConfirmOpen(false);
                                void handleTogglePostOnly(true);
                            },
                        },
                    ]}
                    onClose={() => setPostOnlyConfirmOpen(false)}
                />
            )}
            {questionnairePopup && (
                <Popup
                    title={recipient?.questionnaireStatus !== "not_sent" ? t("recipientPage.resendQuestionnaire") : t("recipientPage.sendQuestionnaire")}
                    message={
                        <p>
                            {t("recipients.sendQuestionnaireTo")}{" "}
                            <strong>{recipient?.firstName} {recipient?.lastName}</strong>
                            {questionnaireEmail
                                ? <> {t("common.at")} <strong>{questionnaireEmail}</strong></>
                                : ` ${t("recipients.noEmail")}`}
                            ?
                        </p>
                    }
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setQuestionnairePopup(false) },
                        {
                            label: questionnaireSending ? t("recipients.sending") : t("recipients.send"),
                            variant: "primary",
                            onClick: sendQuestionnaire,
                        },
                    ]}
                    onClose={() => setQuestionnairePopup(false)}
                />
            )}
            {smsPopup && (
                <Popup
                    title={recipient?.questionnaireStatus !== "not_sent" ? t("recipientPage.resendQuestionnaireSms") : t("recipientPage.sendQuestionnaireSms")}
                    message={
                        <div className="sms-popup">
                            <p className="sms-popup__cost">{t("recipientPage.smsCostNote")}</p>
                            <div className="sms-popup__field">
                                <span className="sms-popup__label">{t("recipientPage.smsLanguageLabel")}</span>
                                <div className="sms-popup__lang-toggle">
                                    <button
                                        type="button"
                                        className={`sms-popup__lang-btn${smsLang === "de" ? " sms-popup__lang-btn--active" : ""}`}
                                        onClick={() => setSmsLang("de")}
                                    >Deutsch</button>
                                    <button
                                        type="button"
                                        className={`sms-popup__lang-btn${smsLang === "en" ? " sms-popup__lang-btn--active" : ""}`}
                                        onClick={() => setSmsLang("en")}
                                    >English</button>
                                </div>
                            </div>
                            <div className="sms-popup__field">
                                <span className="sms-popup__label">{t("recipientPage.smsToLabel")}</span>
                                {recipientPhones.length > 1 ? (
                                    <select
                                        className="sms-popup__phone-select"
                                        value={selectedSmsPhone}
                                        onChange={(e) => setSelectedSmsPhone(e.target.value)}
                                    >
                                        {recipientPhones.map((p) => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <span className="sms-popup__value">{questionnairePhone}</span>
                                )}
                            </div>
                            <div className="sms-popup__preview">
                                <div className="sms-popup__preview-row">
                                    <span className="sms-popup__preview-label">{t("recipientPage.smsTitleLabel")}</span>
                                    <span className="sms-popup__preview-value">{smsPreviewTitle}</span>
                                </div>
                                <div className="sms-popup__preview-row">
                                    <span className="sms-popup__preview-label">{t("recipientPage.smsTextLabel")}</span>
                                    <span className="sms-popup__preview-value sms-popup__preview-value--pre">{smsPreviewText}</span>
                                </div>
                            </div>
                        </div>
                    }
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setSmsPopup(false) },
                        {
                            label: smsSending ? t("recipients.sending") : t("recipients.send"),
                            variant: "primary",
                            onClick: sendQuestionnaireSms,
                        },
                    ]}
                    onClose={() => setSmsPopup(false)}
                />
            )}
            {qrOpen && (
                <div className="qr-modal-overlay" onClick={() => setQrOpen(false)}>
                    <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="qr-modal__header">
                            <span className="qr-modal__title">
                                <TbQrcode /> {t("recipientPage.questionnaireQr")}
                            </span>
                            <button className="qr-modal__close" onClick={() => setQrOpen(false)}>
                                <TbX />
                            </button>
                        </div>
                        <div className="qr-modal__body">
                            <p className="qr-modal__hint">{t("recipientPage.questionnaireQrHint")}</p>
                            {qrLoading && <div className="qr-modal__loading"><span className="skeleton" style={{ width: 280, height: 280, borderRadius: 8 }} /></div>}
                            {qrUrl && <img className="qr-modal__img" src={qrUrl} alt="QR Code" />}
                            <p className="qr-modal__name">{recipient?.firstName} {recipient?.lastName}</p>
                        </div>
                    </div>
                </div>
            )}
            {consentQrOpen && (
                <div className="qr-modal-overlay" onClick={() => setConsentQrOpen(false)}>
                    <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="qr-modal__header">
                            <span className="qr-modal__title">
                                <TbQrcode /> {consentQrChannel === "post" ? "Einwilligung (Post) – QR-Code" : "Einwilligung – QR-Code"}
                            </span>
                            <button className="qr-modal__close" onClick={() => setConsentQrOpen(false)}>
                                <TbX />
                            </button>
                        </div>
                        <div className="qr-modal__body">
                            <p className="qr-modal__hint">
                                {consentQrChannel === "post"
                                    ? "QR-Code scannen, um die postalische Einwilligung digital zu bestätigen."
                                    : "QR-Code scannen, um die Einwilligung digital zu unterschreiben."}
                            </p>
                            {consentQrLoading && <div className="qr-modal__loading"><span className="skeleton" style={{ width: 280, height: 280, borderRadius: 8 }} /></div>}
                            {consentQrUrl && <img className="qr-modal__img" src={consentQrUrl} alt="QR Code" />}
                            <p className="qr-modal__name">{recipient?.firstName} {recipient?.lastName}</p>
                        </div>
                    </div>
                </div>
            )}
            {revokeQrOpen && (
                <div className="qr-modal-overlay" onClick={() => setRevokeQrOpen(false)}>
                    <div className="qr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="qr-modal__header">
                            <span className="qr-modal__title">
                                <TbBan /> {t("recipientPage.requestConsentRevokeQrTitle")}
                            </span>
                            <button className="qr-modal__close" onClick={() => setRevokeQrOpen(false)}>
                                <TbX />
                            </button>
                        </div>
                        <div className="qr-modal__body">
                            <p className="qr-modal__hint">{t("recipientPage.requestConsentRevokeQrHint")}</p>
                            {revokeQrLoading && <div className="qr-modal__loading"><span className="skeleton" style={{ width: 280, height: 280, borderRadius: 8 }} /></div>}
                            {revokeQrUrl && <img className="qr-modal__img" src={revokeQrUrl} alt="QR Code" />}
                            <p className="qr-modal__name">{recipient?.firstName} {recipient?.lastName}</p>
                            <div className="qr-modal__divider">{t("recipientPage.requestConsentRevokeOr")}</div>
                            {recipientPhones.length > 1 && (
                                <select
                                    className="qr-modal__phone-select"
                                    value={selectedSmsPhone}
                                    onChange={(e) => setSelectedSmsPhone(e.target.value)}
                                >
                                    {recipientPhones.map((p) => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            )}
                            <div className="qr-modal__actions">
                                <button
                                    type="button"
                                    className="qr-modal__action-btn"
                                    disabled={!recipient?.email || revokeEmailSending}
                                    onClick={sendConsentRevokeEmail}
                                >
                                    <MdOutlineMail /> {t("recipientPage.sendConsentRevokeEmail")}
                                </button>
                                {recipientPhones.length > 0 && (
                                    <button
                                        type="button"
                                        className="qr-modal__action-btn"
                                        disabled={revokeSmsSending}
                                        onClick={sendConsentRevokeSms}
                                    >
                                        <MdSms /> {t("recipientPage.sendConsentRevokeSms")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <DetailPage
                loading={loading}
                error={error}
                notFoundMessage={t("recipientPage.notFound")}
                icon={<FaUserInjured />}
                accentColor="#6366f1"
                title={pageTitle || "Recipient"}
                titleNode={titleNode}
                breadcrumbActions={nextPatient && (
                    <button
                        className="detail-breadcrumb__parent detail-breadcrumb__action detail-breadcrumb__next"
                        onClick={() => navigate(`/patients/${nextPatient.recipientId}`, { state: { ...(nextPatient.patientId ? { patientId: nextPatient.patientId } : {}), ...(patientRows ? { patientRows } : {}) } })}
                        title={t("recipientPage.nextRecipientTitle", { name: nextPatientName })}
                    >
                        {t("recipientPage.nextRecipient")}: <span className="detail-breadcrumb__next-name">{nextPatientName}</span>{" "}
                        <IoChevronForward className="detail-breadcrumb__sep detail-breadcrumb__sep--next" />
                    </button>
                )}
                meta={
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {recipient?.email && <><MdOutlineMail /> {recipient.email}</>}
                        {recipient?.hasPortalAccount && (
                            <span className="chip chip--success">
                                <TbShieldCheck style={{ marginRight: 4 }} />{t("recipients.portalRegistered")}
                            </span>
                        )}
                        {/* Digital and postal consent are independent — both can show at once. */}
                        {consentDigitalValid && (
                            recipient?.questionnaireSubmittedAt ? (
                                <span
                                    className="chip chip--success chip--clickable"
                                    title={t("recipients.viewQuestionnaire")}
                                    onClick={handleViewQuestionnairePdf}
                                >
                                    <TbClipboardCheck style={{ marginRight: 4 }} />{t("recipients.questionnaireSubmitted")}
                                </span>
                            ) : (
                                <span
                                    className="chip chip--success chip--clickable"
                                    title={t("recipients.viewConsent")}
                                    onClick={() => handleViewConsentPdf("digital")}
                                >
                                    <TbClipboardCheck style={{ marginRight: 4 }} />
                                    {recipient?.consentDigital?.signatureType === "physical"
                                        ? t("recipients.consentManualDigital")
                                        : t("recipients.questionnaireSubmitted")}
                                </span>
                            )
                        )}
                        {consentPostValid && (
                            <span
                                className="chip chip--success chip--clickable"
                                title={t("recipients.viewConsent")}
                                onClick={() => handleViewConsentPdf("post")}
                            >
                                <TbClipboardCheck style={{ marginRight: 4 }} />{t("recipients.consentManualPost")}
                            </span>
                        )}
                        {consentMissingNames.length > 0 && (
                            <span className="chip chip--warning" title={t("recipients.consentMissingForHint")}>
                                <TbAlertTriangle className="recipient-chip-icon" />{t("recipients.consentMissingFor", { names: consentMissingNames.join(", ") })}
                            </span>
                        )}
                        {!consentDigitalValid && recipient?.questionnaireStatus === "sent" && (
                            <span className="chip chip--warning">
                                <TbClipboardList style={{ marginRight: 4 }} />{t("recipients.questionnaireSent")}
                            </span>
                        )}
                    </div>
                }
                headerRight={
                    (showGroupsUI && groupsPatient) || (postEnabled && groupsPatient) ? (
                        <div className="recipient-header-actions">
                            {showGroupsUI && groupsPatient && (
                                <>
                                    {(groupsPatient.groupIds ?? []).map((gid: string) => {
                                        const group = groupsList.find((g) => g.id === gid);
                                        return group ? (
                                            <span key={gid} className="patient-group-chip" style={getTagStyle(group.name) as React.CSSProperties}>
                                                {group.name}
                                            </span>
                                        ) : null;
                                    })}
                                    <button
                                        type="button"
                                        className="recipient-manage-groups-btn"
                                        onClick={() => setAssignGroupsOpen(true)}
                                        title={t("groups.manageGroups")}
                                    >
                                        <TbUsers />
                                    </button>
                                </>
                            )}
                            {postEnabled && groupsPatient && (
                                <button
                                    type="button"
                                    className={`recipient-postonly-toggle${groupsPatient.postOnly ? " recipient-postonly-toggle--active" : ""}`}
                                    title={t("form.postOnlyHint")}
                                    disabled={!canWrite || postOnlySaving}
                                    onClick={() => {
                                        if (groupsPatient.postOnly) {
                                            handleTogglePostOnly(false);
                                        } else {
                                            setPostOnlyConfirmOpen(true);
                                        }
                                    }}
                                >
                                    <TbMailOff style={{ marginRight: 4 }} />
                                    {t("form.postOnly")}
                                </button>
                            )}
                        </div>
                    ) : undefined
                }
                actions={headerActions}
                subheader={unsentInvoices.length > 0 && (
                    <div className="unsent-warning">
                        <MdWarningAmber className="unsent-warning__icon" />
                        <span className="unsent-warning__label">{t("recipientPage.unsentInvoices")}:</span>
                        {unsentInvoices.map((inv, i) => (
                            <span key={inv.id}>
                                <span className="unsent-warning__link" onClick={() => navigate(`/invoices/${inv.id}`)}>
                                    {inv.readableId}
                                </span>
                                {i < unsentInvoices.length - 1 && <span className="unsent-warning__sep">,</span>}
                            </span>
                        ))}
                    </div>
                )}
                tabs={
                    <div className="settings-tabs">
                        {ALL_TABS.map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                className={`settings-tab${activeTab === tab ? " settings-tab--active" : ""}`}
                                onClick={() => setActiveTab(tab)}
                                disabled={tab === "patients" && patientsTabDisabled}
                            >
                                {RECIPIENT_TAB_ICONS[tab]}
                                {tab === "patients" && patientSelected
                                    ? t("recipientPage.tab_recipient")
                                    : t(`recipientPage.tab_${tab}`)}
                                {tab === "patients" && !patientSelected && hasPatient && (
                                    <span className="settings-tab-count">({dependentPatients.length})</span>
                                )}
                                {tab === "invoices" && displayInvoices.length > 0 && (
                                    <span className="settings-tab-count">({displayInvoices.length})</span>
                                )}
                            </button>
                        ))}
                    </div>
                }
            >
                {activeTab === "details" && patientSelected && (
                    <>
                        <DetailCard title={t("form.personal")}>
                            <DetailRow label={t("form.salutationRequired")} value={
                                <select className="recipient-select" value={patSalutation} onChange={(e) => setPatSalutation(e.target.value)} disabled={!canWrite}>
                                    {SALUTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            } />
                            <DetailRow label={t("form.title")} value={
                                <input className="recipient-input" disabled={!canWrite} value={patTitle} maxLength={20}
                                    onChange={(e) => setPatTitle(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setPatTitle(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.firstName")} value={
                                <input className="recipient-input" disabled={!canWrite} value={patFirstName} maxLength={50}
                                    onChange={(e) => setPatFirstName(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setPatFirstName(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.lastName")} value={
                                <input className="recipient-input" disabled={!canWrite} value={patLastName} maxLength={50}
                                    onChange={(e) => setPatLastName(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setPatLastName(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.gender")} value={
                                <select className="recipient-select" value={patGender} onChange={(e) => setPatGender(e.target.value)} disabled={!canWrite}>
                                    <option value="M">{t("form.male")}</option>
                                    <option value="F">{t("form.female")}</option>
                                    <option value="D">{t("form.diverse")}</option>
                                </select>
                            } />
                            <DetailRow label={t("form.birthDate")} value={
                                <input className="recipient-input" disabled={!canWrite} type="date" value={patBirthDate}
                                    onChange={(e) => setPatBirthDate(e.target.value)} {...birthDateBounds()} />
                            } />
                        </DetailCard>

                        <DetailCard title={t("form.personalContact")}>
                            {externalIdRow(activePatient, { value: patExternalId, set: setPatExternalId })}
                            <DetailRow label={t("form.insuranceProvider")} value={
                                <input className="recipient-input recipient-input--wide" disabled={!canWrite} value={insuranceProvider}
                                    onChange={(e) => setInsuranceProvider(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setInsuranceProvider(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.insuranceNumber")} value={
                                <input className="recipient-input" disabled={!canWrite} value={insuranceNumber}
                                    onChange={(e) => setInsuranceNumber(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setInsuranceNumber(trimInput(e.target.value))} />
                            } />
                        </DetailCard>
                    </>
                )}

                {activeTab === "details" && !patientSelected && (
                    <>
                        <DetailCard title={t("form.personal")}>
                            <DetailRow label={t("form.salutationRequired")} value={
                                <select className="recipient-select" value={salutation} onChange={(e) => setSalutation(e.target.value)} disabled={!canWrite}>
                                    {SALUTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            } />
                            <DetailRow label={t("form.title")} value={
                                <input className="recipient-input" disabled={!canWrite} value={title} maxLength={20}
                                    onChange={(e) => setTitle(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setTitle(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.firstName")} value={
                                <input className="recipient-input" disabled={!canWrite} value={firstName} maxLength={50}
                                    onChange={(e) => setFirstName(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setFirstName(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.lastName")} value={
                                <input className="recipient-input" disabled={!canWrite} value={lastName} maxLength={50}
                                    onChange={(e) => setLastName(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setLastName(trimInput(e.target.value))} />
                            } />
                            {selfPatient && (
                                <DetailRow label={t("form.gender")} value={
                                    <select className="recipient-select" value={selfForm.gender} onChange={(e) => selfForm.setGender(e.target.value)} disabled={!canWrite}>
                                        <option value="M">{t("form.male")}</option>
                                        <option value="F">{t("form.female")}</option>
                                        <option value="D">{t("form.diverse")}</option>
                                    </select>
                                } />
                            )}
                            <DetailRow label={t("form.birthDate")} value={
                                <input className="recipient-input" disabled={!canWrite} type="date" value={birthDate}
                                    onChange={(e) => setBirthDate(e.target.value)} {...birthDateBounds()} />
                            } />
                        </DetailCard>

                        {/* Only when the recipient is also a patient — a pure payer has no
                            practice-software record or insurance of their own. Same card as a child's. */}
                        {selfPatient && (
                            <DetailCard title={t("form.personalContact")}>
                                {externalIdRow(selfPatient, { value: selfForm.externalId, set: selfForm.setExternalId })}
                                <DetailRow label={t("form.insuranceProvider")} value={
                                    <input className="recipient-input recipient-input--wide" disabled={!canWrite} value={selfForm.insuranceProvider}
                                        onChange={(e) => selfForm.setInsuranceProvider(sanitizeInput(e.target.value))}
                                        onBlur={(e) => selfForm.setInsuranceProvider(trimInput(e.target.value))} />
                                } />
                                <DetailRow label={t("form.insuranceNumber")} value={
                                    <input className="recipient-input" disabled={!canWrite} value={selfForm.insuranceNumber}
                                        onChange={(e) => selfForm.setInsuranceNumber(sanitizeInput(e.target.value))}
                                        onBlur={(e) => selfForm.setInsuranceNumber(trimInput(e.target.value))} />
                                } />
                            </DetailCard>
                        )}

                        <DetailCard title={<><MdPhone /> {t("form.contactAddress")}</>}>
                            <DetailRow label={t("form.email")} value={
                                <input className="recipient-input recipient-input--wide" disabled={!canWrite} type="email" value={email}
                                    onChange={(e) => setEmail(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setEmail(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.phone")} value={
                                <MultiPhoneInput value={tel} onChange={setTel} disabled={!canWrite} />
                            } />
                            <DetailRow label={t("form.streetAndNumber")} value={
                                <input className="recipient-input" disabled={!canWrite} value={street}
                                    placeholder="Hauptstraße 22a"
                                    onChange={(e) => setStreet(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setStreet(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.plz")} value={
                                <input className="recipient-input recipient-input--short" disabled={!canWrite} value={plz}
                                    onChange={(e) => setPlz(normalizePlz(e.target.value))} />
                            } />
                            <DetailRow label={t("form.city")} value={
                                <CityInput className="recipient-input" disabled={!canWrite} plz={plz} value={city}
                                    onChange={(v) => setCity(sanitizeInput(v))}
                                    onBlur={(v) => setCity(trimInput(v))} />
                            } />
                        </DetailCard>
                    </>
                )}

                {activeTab === "patients" && hasPatient && !patientSelected && (
                    <RecipientPatientsCard
                        patients={dependentPatients}
                        onAddPatient={() => setAddPatientOpen(true)}
                        onEditPatient={(p) => navigate(`/patients/${safeId}`, { state: { patientId: p.id, tab: "details" } })}
                    />
                )}

                {activeTab === "patients" && !hasPatient && (
                    <p className="recipient-page__no-patients">{t("recipientPage.noPatients")}</p>
                )}

                {activeTab === "patients" && hasPatient && (
                    <>
                        <DetailCard title={t("form.personal")}>
                            <DetailRow label={t("form.salutationRequired")} value={
                                <select className="recipient-select" value={salutation} onChange={(e) => setSalutation(e.target.value)} disabled={!canWrite}>
                                    {SALUTATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            } />
                            <DetailRow label={t("form.title")} value={
                                <input className="recipient-input" disabled={!canWrite} value={title} maxLength={20}
                                    onChange={(e) => setTitle(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setTitle(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.firstName")} value={
                                <input className="recipient-input" disabled={!canWrite} value={firstName} maxLength={50}
                                    onChange={(e) => setFirstName(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setFirstName(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.lastName")} value={
                                <input className="recipient-input" disabled={!canWrite} value={lastName} maxLength={50}
                                    onChange={(e) => setLastName(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setLastName(trimInput(e.target.value))} />
                            } />
                        </DetailCard>

                        <DetailCard title={<><MdPhone /> {t("form.contactAddress")}</>}>
                            <DetailRow label={t("form.email")} value={
                                <input className="recipient-input recipient-input--wide" disabled={!canWrite} type="email" value={email}
                                    onChange={(e) => setEmail(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setEmail(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.phone")} value={
                                <MultiPhoneInput value={tel} onChange={setTel} disabled={!canWrite} />
                            } />
                            <DetailRow label={t("form.streetAndNumber")} value={
                                <input className="recipient-input" disabled={!canWrite} value={street}
                                    placeholder="Hauptstraße 22a"
                                    onChange={(e) => setStreet(sanitizeInput(e.target.value))}
                                    onBlur={(e) => setStreet(trimInput(e.target.value))} />
                            } />
                            <DetailRow label={t("form.plz")} value={
                                <input className="recipient-input recipient-input--short" disabled={!canWrite} value={plz}
                                    onChange={(e) => setPlz(normalizePlz(e.target.value))} />
                            } />
                            <DetailRow label={t("form.city")} value={
                                <CityInput className="recipient-input" disabled={!canWrite} plz={plz} value={city}
                                    onChange={(v) => setCity(sanitizeInput(v))}
                                    onBlur={(v) => setCity(trimInput(v))} />
                            } />
                        </DetailCard>
                    </>
                )}

                {activeTab === "invoices" && (
                    <RecipientInvoicesCard
                        invoices={displayInvoices}
                        navigate={navigate}
                    />
                )}

                {activeTab === "questionnaire" && safeId && (
                    <RecipientQuestionnaireTab
                        key={`${recipient?.questionnaireStatus ?? ""}-${recipient?.questionnaireSubmittedAt ?? ""}-${recipient?.consentDigital?.signedAt ?? ""}`}
                        recipientId={safeId}
                        onViewQuestionnairePdf={handleViewQuestionnairePdf}
                        onViewConsentPdf={handleViewConsentPdf}
                        awaitingSubmission={recipient?.questionnaireStatus === "sent"}
                        awaitingSubmissionRecord={recipient?.questionnaireStatus === "submitted"}
                    />
                )}
            </DetailPage>
            {recipient && canWrite && (
                <StickyCornerAction>
                    <button
                        className="recipient-action-btn recipient-action-btn--save"
                        onClick={activeTab === "details" && patientSelected ? handlePatientSave : handleRecipientAndSelfSave}
                        disabled={
                            activeTab === "details" && patientSelected
                                ? (!patientIsDirty || patientSaving)
                                : (!(recipientIsDirty || selfForm.isDirty) || recipientSaving || selfForm.saving)
                        }
                    >
                        <TbDeviceFloppy /> {(activeTab === "details" && patientSelected ? patientSaving : recipientSaving || selfForm.saving) ? t("recipientPage.saving") : t("recipientPage.saveChanges")}
                    </button>
                </StickyCornerAction>
            )}
        </>
    );
}
